// Solar geometry for SeaState — day / twilight / night.
//
// Pure math, no fetch and no DOM. Sun elevation comes from the standard
// low-precision NOAA equations (±~0.5°, ample for shading and minute-level
// sunrise/sunset), so it works at any point, offline, with no extra API call
// or failure mode. Ported verbatim from the v1 single-file app.

export interface Solar {
  /** sun elevation above the horizon, degrees, one per label hour. */
  elevDeg: number[];
  /** darkness level per hour: 0 daylight · 1 civil · 2 nautical · 3 night. */
  level: number[];
  /** sunrise instants, UTC ms (−0.833° up-crossings). */
  rises: number[];
  /** sunset instants, UTC ms (−0.833° down-crossings). */
  sets: number[];
}

/**
 * Sun elevation in degrees above the horizon at a UTC instant, from the
 * standard low-precision NOAA equations. `utcMs` is a true UTC epoch.
 */
export function sunElevation(utcMs: number, lat: number, lon: number): number {
  const rad = Math.PI / 180;
  const n = utcMs / 86400000 + 2440587.5 - 2451545.0; // days since J2000 (UTC)
  const L = (280.46 + 0.9856474 * n) % 360; // mean longitude
  const g = ((357.528 + 0.9856003 * n) % 360) * rad; // mean anomaly
  const lambda = (L + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * rad; // ecliptic long.
  const eps = (23.439 - 0.0000004 * n) * rad; // obliquity
  const decl = Math.asin(Math.sin(eps) * Math.sin(lambda));
  const ra = Math.atan2(Math.cos(eps) * Math.sin(lambda), Math.cos(lambda));
  const gmst = (280.46061837 + 360.98564736629 * n) % 360;
  const h = (gmst + lon) * rad - ra; // local hour angle
  const el = Math.asin(
    Math.sin(lat * rad) * Math.sin(decl) +
      Math.cos(lat * rad) * Math.cos(decl) * Math.cos(h),
  );
  return el / rad; // degrees above horizon
}

/**
 * Darkness level per hour: 0 daylight · 1 civil twilight · 2 nautical · 3 night.
 * Civil twilight (sun 0 to −6°) is the practical "usable light for deck work"
 * band; below −6° a boat operator is working in the dark.
 */
const nightLevel = (el: number): number =>
  el >= -0.833 ? 0 : el >= -6 ? 1 : el >= -12 ? 2 : 3;

/**
 * Per-hour elevation + darkness level, plus minute-accurate sunrise/sunset
 * instants found by interpolating the −0.833° horizon crossings. Times are the
 * Open-Meteo local wall-clock labels ("2026-07-11T14:00"); `utcOffsetSeconds`
 * turns them into true UTC epochs.
 */
export function solarSeries(
  times: string[],
  utcOffsetSeconds: number,
  lat: number,
  lon: number,
): Solar {
  const offsetMs = utcOffsetSeconds * 1000;
  const utc = (t: string): number => new Date(t + ":00Z").getTime() - offsetMs; // local ISO → true UTC ms
  const elev = times.map((t) => sunElevation(utc(t), lat, lon));
  const level = elev.map(nightLevel);
  const cross = (up: boolean): number[] => {
    // up: sunrise crossings; else sunsets
    const out: number[] = [];
    for (let i = 1; i < elev.length; i++) {
      const a = elev[i - 1] + 0.833;
      const b = elev[i] + 0.833;
      if (up ? a < 0 && b >= 0 : a >= 0 && b < 0) {
        const f = a / (a - b); // fractional position between the two hours
        out.push(utc(times[i - 1]) + f * (utc(times[i]) - utc(times[i - 1])));
      }
    }
    return out;
  };
  return { elevDeg: elev, level, rises: cross(true), sets: cross(false) };
}

/**
 * Header line: sunrise → sunset with daylight length for the day containing
 * `nowIndex`, or the polar-day / polar-night fallback. Ported from the v1
 * header logic.
 */
export function sunLine(
  solar: Solar,
  times: string[],
  nowIndex: number,
  utcOffsetSeconds: number,
): string {
  const offsetMs = utcOffsetSeconds * 1000;
  const i0 = Math.max(0, nowIndex);
  const todayStr = times[i0].slice(0, 10);
  const localDate = (ms: number): string =>
    new Date(ms + offsetMs).toISOString().slice(0, 10);
  const localHM = (ms: number): string =>
    new Date(ms + offsetMs).toISOString().slice(11, 16);
  const sr = solar.rises.find((ms) => localDate(ms) === todayStr);
  const ss = solar.sets.find((ms) => localDate(ms) === todayStr);
  if (sr != null && ss != null) {
    const mins = Math.round((ss - sr) / 60000);
    return `☀ ${localHM(sr)} → ${localHM(ss)} · ${Math.floor(mins / 60)}h ${mins % 60}m daylight`;
  }
  return solar.level[i0] === 0 ? "☀ sun up all day" : "🌙 dark all day";
}
